import { useState, useEffect } from 'react';

/**
 * Decodes the payload of a JWT token without verifying the signature.
 * Signature verification is the backend's responsibility.
 * We use this only to read display/UX-relevant claims on the client.
 *
 * @param {string} token - The raw JWT string
 * @returns {Record<string, unknown> | null}
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // Base64Url → Base64 → JSON
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// .NET System.Security.Claims uses the full URI for standard claim types
const ROLE_CLAIM_KEY = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

/**
 * Extracts role(s) from the decoded JWT payload.
 * The role claim can be a single string or an array of strings
 * (e.g. GuildAdmin receives ["GuildAdmin", "Adventurer"]).
 *
 * @param {Record<string, unknown>} payload
 * @returns {string[]}
 */
function extractRoles(payload) {
  if (!payload) return [];
  const raw = payload[ROLE_CLAIM_KEY] || payload['role'];
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
}

/**
 * Central hook for reading the current authenticated user from the JWT.
 *
 * Returns:
 * - `user`          — raw localStorage object (username, avatarUrl, etc.)
 * - `roles`         — string[] of roles from JWT payload
 * - `isLoggedIn`    — token present and not expired
 * - `isAdventurer`  — has "Adventurer" role
 * - `isGuildAdmin`  — has "GuildAdmin" role (also isAdventurer === true)
 * - `isSystemAdmin` — has "SystemAdmin" role (also isAdventurer === true)
 * - `isPartner`     — has "Partner" role
 */
export function useCurrentUser() {
  const [state, setState] = useState(() => computeState());

  useEffect(() => {
    // Re-compute when another tab logs in or out
    const handleStorage = () => setState(computeState());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return state;
}

function computeState() {
  try {
    const token = localStorage.getItem('goodvale_token');
    const userStr = localStorage.getItem('goodvale_user');
    const user = userStr ? JSON.parse(userStr) : null;

    if (!token || !user) {
      return emptyState();
    }

    const payload = decodeJwtPayload(token);
    if (!payload) return emptyState();

    // Check token expiry (exp claim is Unix seconds)
    if (payload.exp && Date.now() / 1000 > payload.exp) {
      return emptyState();
    }

    const roles = extractRoles(payload);

    return {
      user,
      roles,
      isLoggedIn: true,
      isAdventurer: roles.includes('Adventurer'),
      isGuildAdmin: roles.includes('GuildAdmin'),
      isSystemAdmin: roles.includes('SystemAdmin'),
      isPartner: roles.includes('Partner'),
    };
  } catch {
    return emptyState();
  }
}

function emptyState() {
  return {
    user: null,
    roles: [],
    isLoggedIn: false,
    isAdventurer: false,
    isGuildAdmin: false,
    isSystemAdmin: false,
    isPartner: false,
  };
}
